import {
  CAMS,
  COUPLE,
  FILTER_CATEGORIES,
  QUICK_FILTERS,
} from 'enum/helpers';
import { GENDER } from 'enum/profile';
import { checkGenderPreferenceValue } from 'utils/gender';


/**
 * Class @BreadcrumpService for managing array of active filters by analyse input allFilters
 * state to get active filters array as flat array of active filters.
 */
class BreadcrumbService {
  /**
   * Create BreadcrumbService instance.
   *
   */
  constructor() {
    /**
     * Represents a breadcrubm service.
     * @constructor
     * @param {array} filters - Filters based on breadcrumbs are being created of.
     */
    this.filters = [];
  }

  /**
   * Adding active filter.
   * @param {object} filter - Filter to be added in active filters array.
   */
  addFilter = (filter) => {
    this.filters.push(filter);
  };

  /**
   * Get all filters.
   * @return {array} Active filters.
   */
  getFilters = () => this.filters;

  /**
   * Removes all active filters.
   */
  removeAllFilters = () => {
    this.filters = [];
  };

  /**
   * Extracts all active filters from shared filter group.
   * @param {object} allFilters - Input param that represents current state of all filters.
   * @param {function} translate - Standard translation function used in app.
   */
  checkActiveInSharedFilters = (allFilters, translate) => {
    if (allFilters.get('shared')) {
      [...allFilters.get('shared').entries()]
        .filter((filterSection) => !['broadcastType'].includes(filterSection[0]))
        .forEach((fs) => {
          const translationKey = `profile.profile.${FILTER_CATEGORIES[fs[0]]}`;
          [...fs[1].values()].forEach((option) => {
            const isTransBodyType = FILTER_CATEGORIES[fs[0]] === 'transBodyType';
            if (option.get('active')) {
              this.addFilter({
                value: translate(
                  `${translationKey}.${isTransBodyType ? option.get('value').toLowerCase() : option.get('value')}`
                ),
                urlPath: option.get('urlPath'),
              });
            }
          });
        });
    }
  };

  /**
   * Extracts all active filters from contextual filter group.
   * @param {object} allFilters - Input param that represents current state of all filters.
   * @param {string} genderPreference - Gender preference string with posible values from GENDER const object.
   * @param {function} translate - Standard translation function used in app.
   */
  checkActiveInContextualFilters = (allFilters, genderPreference, translate) => {
    let newGenderPreference = '';
    if (genderPreference === GENDER.TRANSGENDER) {
      newGenderPreference = GENDER.SHEMALE;
    } else {
      newGenderPreference = genderPreference;
    }
    if (allFilters.hasIn(['contextual', newGenderPreference])) {
      [...allFilters.getIn(['contextual', newGenderPreference]).entries()].forEach((filterSection) => {
        const translationKey = `profile.profile.${FILTER_CATEGORIES[filterSection[0]]}`;
        [...filterSection[1].values()].forEach((option) => {
          if (option.get('active')) {
            this.addFilter({
              value: translate(`${translationKey}.${option.get('value').toLowerCase()}`),
              urlPath: option.get('urlPath'),
            });
          }
        });
      });
    }
  };

  /**
   * Extracts all active countries from countries filter group.
   * @param {object} allFilters - Input param that represents current state of all filters.
   * @param {function} translate - Standard translation function used in app.
   */
  checkActiveInCountries = (allFilters, translate) => {
    [
      ...allFilters
        .getIn(['locale', 'countries'])
        .filter((cntr) => cntr.get('active'))
        .values(),
    ].forEach((cntr) => {
      this.addFilter({
        value: translate(`countries.${cntr.get('code')}`),
        urlPath: cntr.get('name').replace(' & ', '-and-').replace(' ', '-')
          .toLowerCase(),
      });
    });
  };

  /**
   * Extracts all active languages from languages filter group.
   * @param {object} allFilters - Input param that represents current state of all filters.
   * @param {function} translate - Standard translation function used in app.
   */
  checkActiveInLanguages = (allFilters, translate) => {
    [
      ...allFilters
        .getIn(['locale', 'languages'])
        .filter((lng) => lng.get('active'))
        .values(),
    ].forEach((lng) => {
      this.addFilter({
        value: translate(`languages.${lng.get('code')}`),
        urlPath: lng.get('name').toLowerCase(),
      });
    });
  };

  /**
   * Handle Couple and All genderPreference
   * @param {function} translate - Standard translation function used in app.
   * @param {object} match - Match object from react router, represents data state about active route.
   */

  handleCouplesAndAll = (translate, match) => {
    const paramFilter = match.params.filter && match.params.filter.split('/');
    const genderPreference = match && match.params && match.params.genderPreference;

    const isGenderPrefCouple = checkGenderPreferenceValue(genderPreference) === COUPLE;
    const isGenderPrefAll = checkGenderPreferenceValue(genderPreference) === GENDER.ALL;
    const isGenderPrefCams = genderPreference === CAMS;
    const hasUrlCouple = paramFilter && paramFilter[0] === COUPLE;

    if (isGenderPrefCouple || (isGenderPrefCams && hasUrlCouple)) {
      this.addFilter({
        value: translate('awards.awards.category.couple'),
        urlPath: COUPLE,
      });
    }

    if (isGenderPrefAll) {
      this.addFilter({
        value: translate(`directory.tab.${GENDER.ALL}`),
        urlPath: GENDER.ALL,
      });
    }
  };

  /**
   * Handles all filters based on their group.
   * @param {object} allFilters - Input param that represents current state of all filters.
   * @param {string} genderPreference - Gender preference string with posible values from GENDER const object.
   * @param {function} translate - Standard translation function used in app.
   * @param {object} match - Match object from react router, represents data state about active route.
   */
  handleReceivedFilters = (allFilters, genderPreference, translate, match) => {
    this.removeAllFilters();
    if (allFilters) {
      this.handleCouplesAndAll(translate, match);
      this.checkActiveInSharedFilters(allFilters, translate);
      this.checkActiveInContextualFilters(allFilters, genderPreference, translate);
      this.checkActiveInCountries(allFilters, translate);
      this.checkActiveInLanguages(allFilters, translate);

      /**
       * Handles quick filters which have an impact on url.
       */
      if (allFilters.get(QUICK_FILTERS.newPerformer)) {
        const newPerformer = translate('directory.applied.new').toLowerCase();
        this.addFilter({
          value: newPerformer,
          urlPath: newPerformer,
        });
      }

      if (allFilters.get('hd')) {
        const hd = translate('directory.directory.filters.hd');
        this.addFilter({
          value: hd,
          urlPath: hd.toLowerCase(),
        });
      }

      if (allFilters.get('kiiroo')) {
        const livetouch = translate('directory.directory.filters.livetouch').toLowerCase();
        this.addFilter({
          value: livetouch,
          urlPath: livetouch,
        });
      }

      if (allFilters.get('source')) {
        const mobile = translate('directory.directory.filters.mobile').toLowerCase();
        this.addFilter({
          value: mobile,
          urlPath: mobile,
        });
      }

      /**
       * Handle tags
       */
      const activeTag = allFilters.get('showTag');
      if (activeTag) {
        this.addFilter({
          value: activeTag,
          urlPath: activeTag.toLowerCase(),
        });
      }
    }
  };
}

export const BreadcrumbServiceClass = BreadcrumbService;

/**
 * Exports as default single BreadcrumbService instance to implement singleton.
 */
export default new BreadcrumbService();
